import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Calendar,
  Hash,
  Tag,
  User,
  AlertCircle,
  MessageSquare,
  Edit,
  Send,
  RefreshCw,
} from "lucide-react";
import { useTicket } from 'features/support-ticket/hooks/useTicket';
import { formatDateTime } from 'utils/dateFormatter';
import Layout from 'components/ui/Layout';
import { useAuth } from 'contexts/AuthContext';
import { useRef, useState } from 'react';
import { useUpdateTicket } from 'features/support-ticket/hooks/useUpdateTicket';

const ViewTicketPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
    const { user } = useAuth();
  
  // const { data: response, isLoading, isError, error } = useTicket(id);

  const {
    data: response,
    isLoading,
    isError,
    error,
    refetch,
    isRefetching,
  } = useTicket(id);

    const { mutate: updateTicket, isPending } = useUpdateTicket();

    const [chatMessage, setChatMessage] = useState('');
    const chatEndRef = useRef(null);
  

  const ticket = response?.data;

  const getStatusColor = (status) => {
    const colors = {
      open: 'bg-blue-500/10 text-blue-600 dark:text-blue-400',
      in_progress: 'bg-yellow-500/10 text-yellow-600 dark:text-yellow-400',
      pending_customer: 'bg-orange-500/10 text-orange-600 dark:text-orange-400',
      pending_seller: 'bg-purple-500/10 text-purple-600 dark:text-purple-400',
      resolved: 'bg-green-500/10 text-green-600 dark:text-green-400',
      closed: 'bg-gray-500/10 text-gray-600 dark:text-gray-400',
      reopened: 'bg-red-500/10 text-red-600 dark:text-red-400',
    };
    return colors[status] || 'bg-gray-500/10 text-gray-600 dark:text-gray-400';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      low: 'bg-blue-500/10 text-blue-600 dark:text-blue-400',
      medium: 'bg-yellow-500/10 text-yellow-600 dark:text-yellow-400',
      high: 'bg-orange-500/10 text-orange-600 dark:text-orange-400',
      urgent: 'bg-red-500/10 text-red-600 dark:text-red-400',
    };
    return colors[priority] || 'bg-gray-500/10 text-gray-600 dark:text-gray-400';
  };

  const getCategoryColor = (category) => {
    const colors = {
      booking_issue: 'bg-purple-500/10 text-purple-600 dark:text-purple-400',
      payment_issue: 'bg-green-500/10 text-green-600 dark:text-green-400',
      service_quality: 'bg-blue-500/10 text-blue-600 dark:text-blue-400',
      cancellation_refund: 'bg-red-500/10 text-red-600 dark:text-red-400',
      technical_issue: 'bg-orange-500/10 text-orange-600 dark:text-orange-400',
      account_issue: 'bg-pink-500/10 text-pink-600 dark:text-pink-400',
      other: 'bg-gray-500/10 text-gray-600 dark:text-gray-400',
    };
    return colors[category] || 'bg-gray-500/10 text-gray-600 dark:text-gray-400';
  };

    const handleSendMessage = (e) => {
    e.preventDefault();
    if (!chatMessage.trim()) return;

    const payload = {
      updateFrom: 'Admin',
      message: chatMessage,
      isInternal: false,
    };

    updateTicket(
      { ticketId: ticket._id, data: payload },
      {
        onSuccess: () => {
          setChatMessage('');
        },
      }
    );
  };


  if (isLoading) {
    return (
      <div className='flex items-center justify-center h-screen'>
        <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-primary'></div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className='flex flex-col items-center justify-center h-screen'>
        <div className='text-destructive text-lg mb-4'>Error loading ticket</div>
        <p className='text-muted-foreground mb-4'>{error?.message || 'Something went wrong'}</p>
        <button
          onClick={() => navigate('/support-tickets')}
          className='px-4 py-2 bg-primary text-primary-foreground rounded-md'
        >
          Back to Tickets
        </button>
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className='flex flex-col items-center justify-center h-screen'>
        <div className='text-lg mb-4'>Ticket not found</div>
        <button
          onClick={() => navigate('/support-tickets')}
          className='px-4 py-2 bg-primary text-primary-foreground rounded-md'
        >
          Back to Tickets
        </button>
      </div>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen bg-background">
        <div className="bg-card border-b border-border sticky top-0 z-10">
          <div className="container mx-auto px-4 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => navigate("/support-tickets")}
                  className="p-2 hover:bg-muted rounded-md transition-colors"
                  title="Back to tickets"
                >
                  <ArrowLeft size={20} />
                </button>
                <div>
                  <h1 className="text-2xl font-bold">Ticket Details</h1>
                  <p className="text-sm text-muted-foreground">
                    #{ticket.ticketNumber}
                  </p>
                </div>
              </div>
              <button
                onClick={() => navigate(`/support-tickets/${id}/edit`)}
                className="flex items-center space-x-2 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
              >
                <Edit size={16} />
                <span>Edit Ticket</span>
              </button>
            </div>
          </div>
        </div>

        <div className="container mx-auto px-4 py-6">
          <div className="bg-card border border-border rounded-lg p-6 space-y-6">
            {/* Status Badges */}
            <div className="flex flex-wrap gap-2">
              <span
                className={`px-3 py-1 text-xs font-medium rounded-full ${getStatusColor(ticket.status)}`}
              >
                {ticket.status?.replace(/_/g, " ")?.toUpperCase()}
              </span>
              <span
                className={`px-3 py-1 text-xs font-medium rounded-full ${getPriorityColor(ticket.priority)}`}
              >
                {ticket.priority?.toUpperCase()} PRIORITY
              </span>
              <span
                className={`px-3 py-1 text-xs font-medium rounded-full ${getCategoryColor(ticket.category)}`}
              >
                {ticket.category?.replace(/_/g, " ")?.toUpperCase()}
              </span>
              {ticket.isEscalated && (
                <span className="px-3 py-1 text-xs font-medium rounded-full bg-red-500/10 text-red-600 dark:text-red-400">
                  <AlertCircle size={12} className="inline mr-1" />
                  ESCALATED
                </span>
              )}
            </div>

            {/* Subject & Description */}
            <div className="space-y-4">
              <div>
                <label className="text-xs font-medium text-muted-foreground uppercase">
                  Subject
                </label>
                <p className="text-base font-semibold mt-1">{ticket.subject}</p>
              </div>

              <div>
                <label className="text-xs font-medium text-muted-foreground uppercase">
                  Description
                </label>
                <p className="text-sm mt-1 bg-muted p-4 rounded-lg">
                  {ticket.description}
                </p>
              </div>
            </div>

            {/* Ticket Attachments */}
            {ticket.attachments && ticket.attachments.length > 0 && (
              <div>
                <label className="text-xs font-medium text-muted-foreground uppercase block mb-3">
                  Attachments ({ticket.attachments.length})
                </label>

                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                  {ticket.attachments.map((attachment, index) => {
                    const isImage =
                      attachment.fileType?.startsWith("image/") ||
                      attachment.fileUrl?.match(/\.(jpg|jpeg|png|gif|webp)$/i);

                    return (
                      <div
                        key={attachment._id || index}
                        className="border border-border rounded-lg overflow-hidden bg-muted"
                      >
                        {isImage ? (
                          <a
                            href={attachment.fileUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <img
                              src={attachment.fileUrl}
                              alt={attachment.fileName}
                              className="w-full h-48 object-cover hover:scale-105 transition-transform duration-300"
                            />
                          </a>
                        ) : (
                          <div className="p-4">
                            <a
                              href={attachment.fileUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-2 text-sm font-medium hover:text-primary"
                            >
                              <span>📎</span>
                              <span>{attachment.fileName}</span>
                            </a>
                          </div>
                        )}

                        <div className="p-3 border-t border-border">
                          <p className="text-xs text-muted-foreground truncate">
                            {attachment.fileName}
                          </p>

                          {/* <p className='text-[11px] text-muted-foreground mt-1'>
                {(attachment.fileSize / 1024 / 1024).toFixed(2)} MB
              </p> */}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Customer & Seller Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {ticket.customerId && (
                <div className="space-y-2 bg-muted rounded-lg p-4">
                  <label className="text-xs font-medium text-muted-foreground uppercase flex items-center space-x-1">
                    <User size={14} />
                    <span>Customer</span>
                  </label>

                  <p className="text-sm font-medium">
                    {ticket.customerId?.firstName} {ticket.customerId?.lastName}
                  </p>

                  <p className="text-xs text-muted-foreground">
                    {ticket.customerId?.email}
                  </p>

                  <p className="text-xs text-muted-foreground">
                    {ticket.customerId?.phoneNumber}
                  </p>
                </div>
              )}

              {ticket.sellerId && (
                <div className="space-y-2 bg-muted rounded-lg p-4">
                  <label className="text-xs font-medium text-muted-foreground uppercase flex items-center space-x-1">
                    <User size={14} />
                    <span>Seller</span>
                  </label>

                  <p className="text-sm font-medium">
                    {ticket.sellerId?.firstName} {ticket.sellerId?.lastName}
                  </p>

                  <p className="text-xs text-muted-foreground">
                    {ticket.sellerId?.email}
                  </p>

                  <p className="text-xs text-muted-foreground">
                    {ticket.sellerId?.phoneNumber}
                  </p>
                </div>
              )}
            </div>

            {/* Booking Info */}
            {ticket.bookingId && (
              <div className="space-y-4">
                <div className="bg-muted rounded-lg p-4">
                  <label className="text-xs font-medium text-muted-foreground uppercase mb-3 block">
                    Booking Information
                  </label>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {ticket.serviceId?.name && (
                      <div>
                        <span className="text-xs text-muted-foreground">
                          Service Name:
                        </span>
                        <p className="text-sm font-medium">
                          {ticket.serviceId?.name}
                        </p>
                      </div>
                    )}

                    {ticket.bookingId?.tourId?.title && (
                      <div>
                        <span className="text-xs text-muted-foreground">
                          Tour Title:
                        </span>
                        <p className="text-sm font-medium">
                          {ticket.bookingId?.tourId?.title}
                        </p>
                      </div>
                    )}

                    {ticket.bookingId?.bookingNumber && (
                      <div>
                        <span className="text-xs text-muted-foreground">
                          Booking Number:
                        </span>
                        <p className="text-sm font-medium">
                          {ticket.bookingId.bookingNumber}
                        </p>
                      </div>
                    )}

                    {ticket.bookingId?.status && (
                      <div>
                        <span className="text-xs text-muted-foreground">
                          Status:
                        </span>
                        <p className="text-sm">
                          <span className="px-2 py-0.5 bg-primary/10 text-primary text-xs rounded font-medium">
                            {ticket.bookingId.status
                              .replace(/_/g, " ")
                              .toUpperCase()}
                          </span>
                        </p>
                      </div>
                    )}

                    {ticket.bookingId?.scheduledDate && (
                      <div>
                        <span className="text-xs text-muted-foreground">
                          Scheduled Date:
                        </span>
                        <p className="text-sm font-medium">
                          {new Date(
                            ticket.bookingId.scheduledDate,
                          ).toLocaleDateString("en-US", {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                          })}
                        </p>
                      </div>
                    )}

                    {/* {ticket.bookingId?.scheduledTime && (
          <div>
            <span className='text-xs text-muted-foreground'>Scheduled Time:</span>
            <p className='text-sm font-medium'>{ticket.bookingId.scheduledTime}</p>
          </div>
        )} */}

                    {/* {ticket.bookingId?.duration != null && (
          <div>
            <span className='text-xs text-muted-foreground'>Duration:</span>
            <p className='text-sm font-medium'>{ticket.bookingId.duration} minutes</p>
          </div>
        )} */}

                    {ticket.bookingId?.payment?.paymentStatus && (
                      <div>
                        <span className="text-xs text-muted-foreground">
                          Payment Status:
                        </span>
                        <p className="text-sm">
                          <span className="px-2 py-0.5 bg-blue-500/10 text-blue-600 dark:text-blue-400 text-xs rounded font-medium">
                            {ticket.bookingId.payment.paymentStatus.toUpperCase()}
                          </span>
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Pricing Details */}
                  {ticket.bookingId?.pricing && (
                    <div className="mt-4 pt-4 border-t border-border">
                      <span className="text-xs text-muted-foreground uppercase block mb-2">
                        Pricing Details
                      </span>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
                        {ticket.bookingId.pricing.baseAmount != null && (
                          <div>
                            <span className="text-muted-foreground text-xs">
                              Base Amount:
                            </span>
                            <p className="font-medium">
                              {ticket.bookingId.pricing.currency}{" "}
                              {ticket.bookingId.pricing.baseAmount}
                            </p>
                          </div>
                        )}

                        {/* {ticket.bookingId.pricing.discountAmount > 0 && (
              <div>
                <span className='text-muted-foreground text-xs'>Discount:</span>
                <p className='font-medium text-green-600'>
                  - {ticket.bookingId.pricing.currency} {ticket.bookingId.pricing.discountAmount}
                </p>
              </div>
            )} */}

                        {ticket.bookingId.pricing.couponDiscount > 0 && (
                          <div>
                            <span className="text-muted-foreground text-xs">
                              Coupon Discount:
                            </span>
                            <p className="font-medium text-green-600">
                              - {ticket.bookingId.pricing.currency}{" "}
                              {ticket.bookingId.pricing.couponDiscount}
                            </p>
                          </div>
                        )}

                        {ticket.bookingId.pricing.taxAmount > 0 && (
                          <div>
                            <span className="text-muted-foreground text-xs">
                              Tax:
                            </span>
                            <p className="font-medium">
                              {ticket.bookingId.pricing.currency}{" "}
                              {ticket.bookingId.pricing.taxAmount}
                            </p>
                          </div>
                        )}

                        {ticket.bookingId.pricing.platformFee > 0 && (
                          <div>
                            <span className="text-muted-foreground text-xs">
                              Platform Fee:
                            </span>
                            <p className="font-medium">
                              {ticket.bookingId.pricing.currency}{" "}
                              {ticket.bookingId.pricing.platformFee}
                            </p>
                          </div>
                        )}

                        {ticket.bookingId.pricing.totalAmount != null && (
                          <div className="col-span-2 md:col-span-1">
                            <span className="text-muted-foreground text-xs">
                              Total Amount:
                            </span>
                            <p className="font-bold text-primary text-base">
                              {ticket.bookingId.pricing.currency}{" "}
                              {ticket.bookingId.pricing.totalAmount}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Service Location */}
                  {/* {ticket.bookingId?.serviceLocation && (
        <div className='mt-4 pt-4 border-t border-border'>
          <span className='text-xs text-muted-foreground uppercase block mb-2'>Service Location</span>
          <div className='text-sm'>
            {ticket.bookingId.serviceLocation.type && (
              <p className='font-medium'>{ticket.bookingId.serviceLocation.type.replace(/_/g, ' ')}</p>
            )}
            {ticket.bookingId.serviceLocation && (
              <p className='text-muted-foreground text-xs'>{ticket.bookingId.serviceLocation.addressLine1}  {ticket.bookingId.serviceLocation.addressLine2}</p>
            )}
          </div>
        </div>
      )} */}

                  {/* Notes */}
                  {(ticket.bookingId?.customerNotes ||
                    ticket.bookingId?.sellerNotes ||
                    ticket.bookingId?.adminNotes) && (
                    <div className="mt-4 pt-4 border-t border-border space-y-2">
                      <span className="text-xs text-muted-foreground uppercase block mb-2">
                        Notes
                      </span>
                      {ticket.bookingId.customerNotes && (
                        <div className="bg-blue-500/5 p-2 rounded text-xs">
                          <span className="font-medium text-blue-600 dark:text-blue-400">
                            Customer Note:
                          </span>
                          <p className="mt-1">
                            {ticket.bookingId.customerNotes}
                          </p>
                        </div>
                      )}
                      {ticket.bookingId.sellerNotes && (
                        <div className="bg-purple-500/5 p-2 rounded text-xs">
                          <span className="font-medium text-purple-600 dark:text-purple-400">
                            Seller Note:
                          </span>
                          <p className="mt-1">{ticket.bookingId.sellerNotes}</p>
                        </div>
                      )}
                      {ticket.bookingId.adminNotes && (
                        <div className="bg-green-500/5 p-2 rounded text-xs">
                          <span className="font-medium text-green-600 dark:text-green-400">
                            Admin Note:
                          </span>
                          <p className="mt-1">{ticket.bookingId.adminNotes}</p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Rating */}
                  {(ticket.bookingId?.rating?.customer ||
                    ticket.bookingId?.rating?.seller) && (
                    <div className="mt-4 pt-4 border-t border-border">
                      <span className="text-xs text-muted-foreground uppercase block mb-2">
                        Ratings
                      </span>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {ticket.bookingId.rating.customer && (
                          <div className="bg-blue-500/5 p-3 rounded">
                            <span className="text-xs font-medium text-blue-600 dark:text-blue-400">
                              Customer Rating
                            </span>
                            <div className="flex items-center gap-2 mt-1">
                              <span className="text-lg font-bold">
                                {ticket.bookingId.rating.customer.rating}/5
                              </span>
                              <span className="text-yellow-500">
                                {"★".repeat(
                                  ticket.bookingId.rating.customer.rating,
                                )}
                              </span>
                            </div>
                            {ticket.bookingId.rating.customer.review && (
                              <p className="text-xs mt-2 text-muted-foreground">
                                {ticket.bookingId.rating.customer.review}
                              </p>
                            )}
                          </div>
                        )}
                        {ticket.bookingId.rating.seller && (
                          <div className="bg-purple-500/5 p-3 rounded">
                            <span className="text-xs font-medium text-purple-600 dark:text-purple-400">
                              Seller Rating
                            </span>
                            <div className="flex items-center gap-2 mt-1">
                              <span className="text-lg font-bold">
                                {ticket.bookingId.rating.seller.rating}/5
                              </span>
                              <span className="text-yellow-500">
                                {"★".repeat(
                                  ticket.bookingId.rating.seller.rating,
                                )}
                              </span>
                            </div>
                            {ticket.bookingId.rating.seller.review && (
                              <p className="text-xs mt-2 text-muted-foreground">
                                {ticket.bookingId.rating.seller.review}
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Cancellation Info */}
                  {ticket.bookingId?.cancellation && (
                    <div className="mt-4 pt-4 border-t border-border">
                      <div className="bg-red-500/5 border border-red-500/20 rounded p-3">
                        <span className="text-xs font-medium text-red-600 dark:text-red-400 uppercase">
                          Cancellation
                        </span>
                        {ticket.bookingId.cancellation.cancellationReason && (
                          <p className="text-xs mt-2">
                            <span className="font-medium">Reason:</span>{" "}
                            {ticket.bookingId.cancellation.cancellationReason}
                          </p>
                        )}
                        {ticket.bookingId.cancellation.cancelledAt && (
                          <p className="text-xs mt-1 text-muted-foreground">
                            Cancelled at:{" "}
                            {formatDateTime(
                              ticket.bookingId.cancellation.cancelledAt,
                            )}
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Rejection Info */}
                  {ticket.bookingId?.rejection && (
                    <div className="mt-4 pt-4 border-t border-border">
                      <div className="bg-orange-500/5 border border-orange-500/20 rounded p-3">
                        <span className="text-xs font-medium text-orange-600 dark:text-orange-400 uppercase">
                          Rejection
                        </span>
                        {ticket.bookingId.rejection.rejectedBy && (
                          <p className="text-xs mt-2">
                            <span className="font-medium">Rejected by:</span>{" "}
                            {ticket.bookingId.rejection.rejectedBy?.name}
                            {ticket.bookingId.rejection.rejectedBy
                              ?.userType && (
                              <>
                                {" "}
                                (
                                {ticket.bookingId.rejection.rejectedBy.userType}
                                )
                              </>
                            )}
                          </p>
                        )}
                        {ticket.bookingId.rejection.rejectionReason && (
                          <p className="text-xs mt-1">
                            <span className="font-medium">Reason:</span>{" "}
                            {ticket.bookingId.rejection.rejectionReason}
                          </p>
                        )}
                        {ticket.bookingId.rejection.rejectedAt && (
                          <p className="text-xs mt-1 text-muted-foreground">
                            Rejected at:{" "}
                            {formatDateTime(
                              ticket.bookingId.rejection.rejectedAt,
                            )}
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Staff Assignment History */}
                  {ticket.bookingId?.staffAssignmentHistory?.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-border">
                      <span className="text-xs text-muted-foreground uppercase block mb-2">
                        Staff Assignment History (
                        {ticket.bookingId.staffAssignmentHistory.length})
                      </span>
                      <div className="space-y-2 max-h-48 overflow-y-auto custom-scrollbar">
                        {ticket.bookingId.staffAssignmentHistory.map(
                          (assignment, index) => (
                            <div
                              key={assignment._id || index}
                              className="bg-muted rounded p-3 text-xs"
                            >
                              <div className="flex items-center justify-between mb-2">
                                <span className="font-medium">
                                  Assignment #{index + 1}
                                </span>
                                {assignment.status && (
                                  <span
                                    className={`px-2 py-0.5 text-[10px] font-medium rounded ${
                                      assignment.status === "accepted"
                                        ? "bg-green-500/20 text-green-600 dark:text-green-400"
                                        : assignment.status === "rejected"
                                          ? "bg-red-500/20 text-red-600 dark:text-red-400"
                                          : "bg-yellow-500/20 text-yellow-600 dark:text-yellow-400"
                                    }`}
                                  >
                                    {assignment.status.toUpperCase()}
                                  </span>
                                )}
                              </div>
                              <div className="space-y-1 text-muted-foreground">
                                {assignment.staffId && (
                                  <p>
                                    <span className="font-medium text-foreground">
                                      Staff ID:
                                    </span>{" "}
                                    {assignment.staffId}
                                  </p>
                                )}
                                {assignment.assignedBy && (
                                  <p>
                                    <span className="font-medium text-foreground">
                                      Assigned By:
                                    </span>{" "}
                                    {assignment.assignedBy}
                                  </p>
                                )}
                                {assignment.assignedAt && (
                                  <p>
                                    <span className="font-medium text-foreground">
                                      Assigned At:
                                    </span>{" "}
                                    {formatDateTime(assignment.assignedAt)}
                                  </p>
                                )}
                                {assignment.acceptedAt && (
                                  <p>
                                    <span className="font-medium text-foreground">
                                      Accepted At:
                                    </span>{" "}
                                    {formatDateTime(assignment.acceptedAt)}
                                  </p>
                                )}
                                {assignment.rejectedAt && (
                                  <p>
                                    <span className="font-medium text-foreground">
                                      Rejected At:
                                    </span>{" "}
                                    {formatDateTime(assignment.rejectedAt)}
                                  </p>
                                )}
                              </div>
                            </div>
                          ),
                        )}
                      </div>
                    </div>
                  )}

                  {/* Reschedule History */}
                  {ticket.bookingId?.rescheduleHistory?.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-border">
                      <span className="text-xs text-muted-foreground uppercase block mb-2">
                        Reschedule History (
                        {ticket.bookingId.rescheduleHistory.length})
                      </span>
                      <div className="space-y-2 max-h-48 overflow-y-auto custom-scrollbar">
                        {ticket.bookingId.rescheduleHistory.map(
                          (reschedule, index) => (
                            <div
                              key={reschedule._id || index}
                              className="bg-muted rounded p-3 text-xs"
                            >
                              <div className="flex items-center justify-between mb-2">
                                <span className="font-medium">
                                  Reschedule #{index + 1}
                                </span>
                                {reschedule.status && (
                                  <span
                                    className={`px-2 py-0.5 text-[10px] font-medium rounded ${
                                      reschedule.status === "approved"
                                        ? "bg-green-500/20 text-green-600 dark:text-green-400"
                                        : reschedule.status === "rejected"
                                          ? "bg-red-500/20 text-red-600 dark:text-red-400"
                                          : "bg-yellow-500/20 text-yellow-600 dark:text-yellow-400"
                                    }`}
                                  >
                                    {reschedule.status.toUpperCase()}
                                  </span>
                                )}
                              </div>
                              <div className="space-y-1 text-muted-foreground">
                                {(reschedule.requestedBy ||
                                  reschedule.requestedById) && (
                                  <p>
                                    <span className="font-medium text-foreground">
                                      Requested By:
                                    </span>{" "}
                                    {reschedule.requestedBy}
                                    {reschedule.requestedById && (
                                      <> ({reschedule.requestedById})</>
                                    )}
                                  </p>
                                )}
                                {reschedule.oldScheduledDate && (
                                  <p>
                                    <span className="font-medium text-foreground">
                                      Old Date:
                                    </span>{" "}
                                    {new Date(
                                      reschedule.oldScheduledDate,
                                    ).toLocaleDateString("en-US", {
                                      year: "numeric",
                                      month: "short",
                                      day: "numeric",
                                    })}
                                  </p>
                                )}
                                {reschedule.newScheduledDate && (
                                  <p>
                                    <span className="font-medium text-foreground">
                                      New Date:
                                    </span>{" "}
                                    {new Date(
                                      reschedule.newScheduledDate,
                                    ).toLocaleDateString("en-US", {
                                      year: "numeric",
                                      month: "short",
                                      day: "numeric",
                                    })}
                                  </p>
                                )}
                                {reschedule.requestedAt && (
                                  <p>
                                    <span className="font-medium text-foreground">
                                      Requested At:
                                    </span>{" "}
                                    {formatDateTime(reschedule.requestedAt)}
                                  </p>
                                )}
                                {reschedule.respondedAt && (
                                  <p>
                                    <span className="font-medium text-foreground">
                                      Responded At:
                                    </span>{" "}
                                    {formatDateTime(reschedule.respondedAt)}
                                  </p>
                                )}
                                {reschedule.reason && (
                                  <p>
                                    <span className="font-medium text-foreground">
                                      Reason:
                                    </span>{" "}
                                    {reschedule.reason}
                                  </p>
                                )}
                              </div>
                            </div>
                          ),
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Assignment Info */}
            {ticket.assignedTo && (
              <div className="bg-muted rounded-lg p-4">
                <label className="text-xs font-medium text-muted-foreground uppercase">
                  Currently Assigned To
                </label>
                <p className="text-sm font-medium mt-2">
                  {ticket.assignedTo?.firstName} {ticket.assignedTo?.lastName}
                </p>
                <p className="text-xs text-muted-foreground">
                  {ticket.assignedTo?.email}
                </p>
              </div>
            )}

            {/* Assignment History */}
            {ticket.assignmentHistory &&
              ticket.assignmentHistory.length > 0 && (
                <div className="space-y-3">
                  <label className="text-xs font-medium text-muted-foreground uppercase flex items-center space-x-1">
                    <User size={14} />
                    <span>
                      Assignment History ({ticket.assignmentHistory.length})
                    </span>
                  </label>
                  <div className="space-y-2 max-h-48 overflow-y-auto custom-scrollbar">
                    {ticket.assignmentHistory.map((assignment, index) => (
                      <div
                        key={assignment._id || index}
                        className="bg-muted rounded-lg p-3 text-sm"
                      >
                        <div className="flex items-center justify-between mb-1">
                          <p className="font-medium">
                            Assigned to: {assignment.assignedTo?.firstName}{" "}
                            {assignment.assignedTo?.lastName} (
                            {assignment.assignedTo?.email})
                          </p>
                          <span
                            className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                              assignment.status === "accepted"
                                ? "bg-green-500/10 text-green-600 dark:text-green-400"
                                : assignment.status === "rejected"
                                  ? "bg-red-500/10 text-red-600 dark:text-red-400"
                                  : "bg-yellow-500/10 text-yellow-600 dark:text-yellow-400"
                            }`}
                          >
                            {assignment.status}
                          </span>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          By: {assignment.assignedBy?.firstName}{" "}
                          {assignment.assignedBy?.lastName} (
                          {assignment.assignedBy?.email})
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Assigned at: {formatDateTime(assignment.assignedAt)}
                        </p>
                        {assignment.acceptedAt && (
                          <p className="text-xs text-muted-foreground">
                            Accepted at: {formatDateTime(assignment.acceptedAt)}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

            {/* Messages */}
            {/* {ticket.messages && ticket.messages.length > 0 && (
              <div className='space-y-3'>
                <label className='text-xs font-medium text-muted-foreground uppercase flex items-center space-x-1'>
                  <MessageSquare size={14} />
                  <span>Comments ({ticket.messages.length})</span>
                </label>
                <div className='space-y-3 max-h-64 overflow-y-auto custom-scrollbar'>
                  {ticket.messages.map((message, index) => {
                    const isAdmin = message.adminId;
                    const isCustomer = message.customerId;
                    const isSeller = message.sellerId;

                    const senderName = isAdmin
                      ? message.adminId.name || 'Admin'
                      : isCustomer
                      ? `${message.customerId?.firstName} ${message.customerId?.lastName}`
                      : `${message.sellerId?.firstName} ${message.sellerId?.lastName}`;

                    const userType = isAdmin ? 'Admin' : isCustomer ? 'Customer' : 'Seller';

                    const profilePhoto = isAdmin
                      ? message.adminId?.profilePhoto
                      : isCustomer
                      ? message.customerId?.profilePhoto
                      : message.sellerId?.profilePhoto;

                    return (
                      <div key={message._id || index} className='bg-muted rounded-lg p-4'>
                        <div className='flex items-start gap-3 mb-2'>
                          <div className='flex-shrink-0'>
                            {profilePhoto ? (
                              <img
                                src={profilePhoto}
                                alt={senderName}
                                className='w-10 h-10 rounded-full object-cover border border-border'
                              />
                            ) : (
                              <div className='w-10 h-10 rounded-full bg-primary/10 border border-border flex items-center justify-center text-sm font-medium'>
                                {senderName?.charAt(0)?.toUpperCase() || '?'}
                              </div>
                            )}
                          </div>

                          <div className='flex-1'>
                            <div className='flex items-center justify-between mb-1'>
                              <div className='flex items-center gap-2'>
                                <p className='text-xs font-medium'>
                                  {userType}: {senderName}
                                </p>
                                {message.isInternal && (
                                  <span className='px-2 py-0.5 bg-yellow-500/10 text-yellow-600 text-xs rounded'>
                                    Internal
                                  </span>
                                )}
                              </div>
                              <p className='text-xs text-muted-foreground'>{formatDateTime(message.sentAt)}</p>
                            </div>
                            <p className='text-sm mb-2'>{message.message}</p>

                            {message.attachments && message.attachments.length > 0 && (
                              <div className='mt-2 space-y-2'>
                                {message.attachments.map((attachment, attIndex) => (
                                  <div key={attIndex}>
                                    {attachment.type?.startsWith('image/') ||
                                    attachment.url?.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? (
                                      <a
                                        href={attachment.url}
                                        target='_blank'
                                        rel='noopener noreferrer'
                                        className='block rounded overflow-hidden border border-border hover:border-primary/50 transition-colors'
                                      >
                                        <img
                                          src={attachment.url}
                                          alt={attachment.fileName || 'Attachment'}
                                          className='max-w-full max-h-64 object-contain'
                                        />
                                      </a>
                                    ) : (
                                      <a
                                        href={attachment.url}
                                        target='_blank'
                                        rel='noopener noreferrer'
                                        className='inline-flex items-center gap-2 px-3 py-2 bg-primary/10 border border-primary/20 rounded text-xs hover:bg-primary/20 transition-colors'
                                      >
                                        <span>📎</span>
                                        <span className='font-medium'>{attachment.fileName || 'Attachment'}</span>
                                      </a>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )} */}

            <div className="space-y-3">
              {/* <label className='text-xs font-medium text-muted-foreground uppercase flex items-center space-x-1'>
                  <MessageSquare size={14} />
                  <span>Messages ({ticket.messages?.length || 0})</span>
                </label> */}

              <div className="flex items-center justify-between">
                <label className="text-xs font-medium text-muted-foreground uppercase flex items-center space-x-1">
                  <MessageSquare size={14} />
                  <span>Messages ({ticket.messages?.length || 0})</span>
                </label>

                <button
                  type="button"
                  onClick={refetch}
                  disabled={isRefetching}
                  className="p-1.5 rounded-md border border-border hover:bg-muted transition-colors disabled:opacity-50"
                  title="Refresh messages"
                >
                  <RefreshCw
                    size={15}
                    className={isRefetching ? "animate-spin" : ""}
                  />
                </button>
              </div>

              <div className="space-y-3 max-h-96 overflow-y-auto custom-scrollbar">
                {ticket.messages && ticket.messages.length > 0 ? (
                  ticket.messages.map((message, index) => {
                    const isAdmin = message.adminId;
                    const isCustomer = message.customerId;
                    const isCurrentUser =
                      isAdmin && message.adminId?._id === user?._id;

                    const senderName = isAdmin
                      ? `${message.adminId?.firstName || ""} ${message.adminId?.lastName || ""}`.trim()
                      : isCustomer
                        ? `${message.customerId?.firstName || ""} ${message.customerId?.lastName || ""}`.trim()
                        : `${message.sellerId?.firstName || ""} ${message.sellerId?.lastName || ""}`.trim();

                    const userType = isAdmin
                      ? "Admin"
                      : isCustomer
                        ? "Customer"
                        : "Seller";

                    const profilePhoto = isAdmin
                      ? message.adminId?.profilePhoto
                      : isCustomer
                        ? message.customerId?.profilePhoto
                        : message.sellerId?.profilePhoto;

                    return (
                      <div
                        key={message._id || index}
                        className="bg-muted rounded-lg p-4"
                      >
                        <div className="flex items-start gap-3 mb-2">
                          <div className="flex-shrink-0">
                            {profilePhoto ? (
                              <img
                                src={profilePhoto}
                                alt={senderName}
                                className="w-10 h-10 rounded-full object-cover border border-border"
                              />
                            ) : (
                              <div className="w-10 h-10 rounded-full bg-primary/10 border border-border flex items-center justify-center text-sm font-medium">
                                {senderName?.charAt(0)?.toUpperCase() || "?"}
                              </div>
                            )}
                          </div>

                          <div className="flex-1">
                            <div className="flex items-center justify-between mb-1">
                              <div className="flex items-center gap-2">
                                <p className="text-xs font-medium">
                                  {userType}:{" "}
                                  {isCurrentUser ? "You" : senderName}
                                </p>
                                {message.isInternal && (
                                  <span className="px-2 py-0.5 bg-yellow-500/10 text-yellow-600 text-xs rounded">
                                    Internal
                                  </span>
                                )}
                              </div>
                              <p className="text-xs text-muted-foreground">
                                {formatDateTime(message.sentAt)}
                              </p>
                            </div>

                            <p className="text-sm mb-2 break-words whitespace-pre-wrap">
                              {message.message}
                            </p>

                            {message.attachments &&
                              message.attachments.length > 0 && (
                                <div className="mt-2 space-y-2">
                                  {message.attachments.map(
                                    (attachment, attIndex) => (
                                      <div key={attIndex}>
                                        {attachment.type?.startsWith(
                                          "image/",
                                        ) ||
                                        attachment.url?.match(
                                          /\.(jpg|jpeg|png|gif|webp)$/i,
                                        ) ? (
                                          <a
                                            href={attachment.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="block rounded overflow-hidden border border-border hover:border-primary/50 transition-colors"
                                          >
                                            <img
                                              src={attachment.url}
                                              alt={
                                                attachment.fileName ||
                                                "Attachment"
                                              }
                                              className="max-w-full max-h-64 object-contain"
                                            />
                                          </a>
                                        ) : (
                                          <a
                                            href={attachment.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-flex items-center gap-2 px-3 py-2 bg-primary/10 border border-primary/20 rounded text-xs hover:bg-primary/20 transition-colors"
                                          >
                                            <span>📎</span>
                                            <span className="font-medium">
                                              {attachment.fileName ||
                                                "Attachment"}
                                            </span>
                                          </a>
                                        )}
                                      </div>
                                    ),
                                  )}
                                </div>
                              )}
                          </div>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="text-center text-muted-foreground text-sm py-8 bg-muted rounded-lg">
                    No messages yet
                  </div>
                )}
                <div ref={chatEndRef} />
              </div>

              <div className="pt-3 border-t border-border">
                <div className="flex items-end space-x-2">
                  <textarea
                    value={chatMessage}
                    onChange={(e) => setChatMessage(e.target.value)}
                    placeholder="Type a message..."
                    rows={2}
                    className="flex-1 px-3 py-2 text-sm border border-border rounded-md bg-background resize-none"
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        handleSendMessage(e);
                      }
                    }}
                  />
                  <button
                    type="button"
                    onClick={handleSendMessage}
                    disabled={!chatMessage.trim() || isPending}
                    className="p-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors disabled:opacity-50"
                  >
                    <Send size={18} />
                  </button>
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Press Enter to send, Shift+Enter for new line
                </p>
              </div>
            </div>

            {/* Resolution */}
            {ticket.resolution && (
              <div className="bg-green-500/5 border border-green-500/20 rounded-lg p-4 space-y-2">
                <label className="text-xs font-medium text-green-600 dark:text-green-400 uppercase">
                  Resolution
                </label>
                <p className="text-sm">{ticket.resolution.resolutionNote}</p>
                {ticket.resolution.customerSatisfaction && (
                  <p className="text-xs text-muted-foreground">
                    Customer Satisfaction:{" "}
                    {ticket.resolution.customerSatisfaction}/5
                  </p>
                )}
                {ticket.resolution.customerFeedback && (
                  <p className="text-xs text-muted-foreground">
                    Feedback: {ticket.resolution.customerFeedback}
                  </p>
                )}
              </div>
            )}

            {/* Escalation Info */}
            {ticket.isEscalated && ticket.escalationReason && (
              <div className="bg-red-500/5 border border-red-500/20 rounded-lg p-4">
                <label className="text-xs font-medium text-red-600 dark:text-red-400 uppercase">
                  Escalation Reason
                </label>
                <p className="text-sm mt-2">{ticket.escalationReason}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Escalated at: {formatDateTime(ticket.escalatedAt)}
                </p>
              </div>
            )}

            {/* Timestamps */}
            {/* <div className='grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t border-border'>
              <div className='space-y-1'>
                <label className='text-xs font-medium text-muted-foreground uppercase flex items-center space-x-1'>
                  <Calendar size={14} />
                  <span>Opened At</span>
                </label>
                <p className='text-sm'>{formatDateTime(ticket.openedAt)}</p>
              </div>

              {ticket.lastResponseAt && (
                <div className='space-y-1'>
                  <label className='text-xs font-medium text-muted-foreground uppercase flex items-center space-x-1'>
                    <Calendar size={14} />
                    <span>Last Response</span>
                  </label>
                  <p className='text-sm'>{formatDateTime(ticket.lastResponseAt)}</p>
                </div>
              )}

              {ticket.closedAt && (
                <div className='space-y-1'>
                  <label className='text-xs font-medium text-muted-foreground uppercase flex items-center space-x-1'>
                    <Calendar size={14} />
                    <span>Closed At</span>
                  </label>
                  <p className='text-sm'>{formatDateTime(ticket.closedAt)}</p>
                </div>
              )}
            </div> */}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ViewTicketPage;
